import React from 'react';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import usePageMeta from '../utils/usePageMeta';

function Section({ title, children }) {
  return (
    <div className="mb-10">
      <h2 className="text-navy-900 font-semibold text-lg mb-3">{title}</h2>
      <div className="text-gray-600 text-sm leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

export default function Terms() {
  usePageMeta(
    'Terms of Service',
    'The terms governing use of the Tactmark platform, including subscriptions, cancellation, and acceptable use.'
  );
  return (
    <div className="min-h-screen bg-white">
      <PublicNav />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-navy-600 text-xs font-semibold tracking-widest uppercase mb-4">Legal</p>
        <h1 className="font-serif text-4xl text-navy-900 leading-tight mb-3">Terms of Service</h1>
        <p className="text-gray-400 text-sm mb-12">Last updated: March 2025</p>

        <Section title="Account use">
          <p>
            By creating a Tactmark account, you confirm that you are authorized to represent the
            organization you register and that the information you provide is accurate. Each
            organization account is intended for use by one club, academy, federation, school,
            analysis company, or solo coach.
          </p>
        </Section>

        <Section title="Acceptable use">
          <p>
            You agree to use Tactmark only for legitimate football/soccer performance analysis
            purposes. You may not use the platform to store or transmit unlawful content, attempt
            to access another organization's data, or attempt to disrupt or reverse-engineer the
            service.
          </p>
        </Section>

        <Section title="Responsibility for uploaded content">
          <p>
            Your organization is responsible for all match footage, notes, player data, and any
            other content it uploads or enters into Tactmark, and must have the right to use and
            store that content.
          </p>
        </Section>

        <Section title="No guarantee of match outcome or coaching replacement">
          <p>
            Tactmark provides structured post-match intelligence based on the information your
            organization submits. It does not guarantee any specific match outcome, and it is not
            a replacement for the professional judgment of coaching and analysis staff. Reports
            are intended to support, not replace, coaching decisions.
          </p>
        </Section>

        <Section title="Subscription and trial terms">
          <p>
            Tactmark offers a 14-day free trial on eligible plans. After the trial period ends,
            continued use of paid features requires an active subscription. Subscription fees are
            billed in advance on a monthly or annual basis, as selected at signup.
          </p>
        </Section>

        <Section title="Tactmark Federation">
          <p>
            Tactmark Federation is contract-provisioned. Access is enabled for an organization only
            under a Federation agreement or written commercial arrangement with Tactmark, and may
            be limited by participating programs, teams, user roles, and contract term.
          </p>
          <p>
            The customer remains responsible for lawful use of player, youth, academy, and staff
            data inside Federation, including any required consents, safeguarding policies, and
            rights to store or reference match footage used as evidence.
          </p>
        </Section>

        <Section title="Cancellation">
          <p>
            You may cancel your subscription at any time from your account settings. Cancellation
            will take effect at the end of the current billing period; we do not provide partial
            refunds for unused time within a billing period except where required by law.
          </p>
        </Section>

        <Section title="Limitation of liability">
          <p>
            To the maximum extent permitted by law, Tactmark and its operators are not liable for
            any indirect, incidental, or consequential damages arising from use of the platform,
            including decisions made based on generated reports.
          </p>
        </Section>

        <Section title="Prohibited misuse">
          <p>
            You may not use Tactmark to harass, defame, or make unsubstantiated public claims
            about players, coaches, or staff. Reports are intended for internal coaching and
            analysis use.
          </p>
        </Section>

        <Section title="Changes to these terms">
          <p>
            We may update these terms from time to time. Continued use of Tactmark after changes
            take effect constitutes acceptance of the updated terms.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Tactmark is a US-based software company operating from the United States.
            Questions about these terms can be directed to{' '}
            <a href="mailto:hello@tactmark.com" className="text-navy-700 underline">hello@tactmark.com</a>.
          </p>
        </Section>
      </div>
      <PublicFooter />
    </div>
  );
}
